import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import RuleImporter from '../RuleImporter';
import { ImportWizardModal } from '../Modals';
import ActionsMenu from '../../../../components/ActionsMenu';
import SelectorSearchSortBar from '../../../../components/SelectorSearchSortBar';
import { sortByName } from '../../../../utils/sortingUtils';
import { rulesetHasEmptyCells, filterRuleSets, isRulesetFilterActive, extractCellValuesFromRuleSet, extractCellValuesByHeaderFromRuleSet, getRuleSetMatchHits, buildSearchHighlight } from '../../utils';
import RulesetAdvancedSearch from './RulesetAdvancedSearch';
import RuleSetSearchHitHint from './RuleSetSearchHitHint';
import { useToolAccess } from '../../../../tools/ToolAccessContext';

/**
 * RuleManagerSidebar - Left sidebar for rule management
 * Contains family selector, ruleset list, search, and import functionality
 */
export default function RuleManagerSidebar({
    families,
    ruleSets,
    activeRuleSetId,
    currentContext,
    rulesetSearchQuery,
    rulesetSearchMode = 'all',
    rulesetFeatureFilters = [],
    rulesetValueFilters = [],
    rulesetSortBy,
    rulesetSortOrder,
    activeFamily,
    handleOpenModal,
    handleOpenCreateFolder,
    handleOpenRenameFolder,
    handleOpenCreateRulesetInFolder,
    handleDeleteFolder,
    handleMoveRuleSetToFolder,
    setActiveRuleSetId,
    setRulesetSearchQuery,
    setRulesetSearchMode,
    setRulesetFeatureFilters,
    setRulesetValueFilters,
    setSearchHighlight,
    setRulesetSortBy,
    setRulesetSortOrder,
    setCurrentContext,
    loadMenu,
    featuresMetadata,
    activeRuleSetData,
    onDataChanged,
    handleCheckConsistency,
    draftRuleSetData,
    handleCopyRuleSetToClipboard,
    handleOpenAddComment,
    handleOpenRenameRuleSet,
    handleDeleteRuleSet,
    reloadActiveRuleSet,
    ruleSetNotes = []
}) {
    const navigate = useNavigate();
    const { canEditCurrent } = useToolAccess();
    const [searchOpen, setSearchOpen] = useState(false);
    const [sortOpen, setSortOpen] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const productActionsRef = useRef(null);
    const [overwriteTarget, setOverwriteTarget] = useState(null);
    const [dropTargetKey, setDropTargetKey] = useState(null);
    const [draggingFromFolderId, setDraggingFromFolderId] = useState(null);
    const [expandedFolderIds, setExpandedFolderIds] = useState(() => new Set());
    const UNFILED_DROP = '__unfiled__';

    const allRuleSets = useMemo(() => {
        if (!activeFamily || !activeFamily.ruleSets || activeFamily.ruleSets.length === 0) {
            return [];
        }

        return activeFamily.ruleSets.map(rs => {
            if (draftRuleSetData && String(draftRuleSetData.id) === String(rs.id)) {
                const liveComments = (ruleSetNotes || [])
                    .map(note => String(note?.text ?? note?.Text ?? '').trim())
                    .filter(Boolean);
                return {
                    ...rs,
                    headers: Array.isArray(draftRuleSetData.headers) ? draftRuleSetData.headers : rs.headers,
                    cellValues: extractCellValuesFromRuleSet(draftRuleSetData),
                    cellValuesByHeader: extractCellValuesByHeaderFromRuleSet(draftRuleSetData),
                    comments: liveComments.length > 0 ? liveComments : (rs.comments || rs.Comments || [])
                };
            }
            return rs;
        });
    }, [activeFamily, draftRuleSetData, ruleSetNotes]);

    const featureOptions = useMemo(() => {
        const fromFamily = activeFamily?.features || [];
        const fromMeta = (featuresMetadata || []).filter(f => f.isGlobal);
        return [...fromFamily, ...fromMeta];
    }, [activeFamily, featuresMetadata]);

    const filterActive = isRulesetFilterActive(rulesetSearchQuery, rulesetFeatureFilters, rulesetValueFilters);

    // Compute filtered and sorted rulesets
    const sortedRuleSets = useMemo(() => {
        let list = filterRuleSets(allRuleSets, {
            mode: rulesetSearchMode,
            query: rulesetSearchQuery,
            featureFilters: rulesetFeatureFilters,
            valueFilters: rulesetValueFilters
        });

        list = [...list];
        list.sort((a, b) => {
            let valA = a[rulesetSortBy] || '';
            let valB = b[rulesetSortBy] || '';

            if (rulesetSortBy === 'createdAt' || rulesetSortBy === 'updatedAt') {
                valA = new Date(valA).getTime();
                valB = new Date(valB).getTime();
            } else if (typeof valA === 'string') {
                valA = valA.toLowerCase();
                valB = String(valB).toLowerCase();
            }

            if (valA < valB) return rulesetSortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return rulesetSortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        return list;
    }, [allRuleSets, rulesetSearchQuery, rulesetSearchMode, rulesetFeatureFilters, rulesetValueFilters, rulesetSortBy, rulesetSortOrder]);

    const familyFolders = useMemo(() => {
        const raw = activeFamily?.folders || activeFamily?.Folders || [];
        return [...raw].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
    }, [activeFamily]);

    const groupedRuleSets = useMemo(() => {
        const byFolder = new Map(familyFolders.map(folder => [String(folder.id), []]));
        const unfiled = [];
        sortedRuleSets.forEach(rs => {
            const folderId = rs.folderId ?? rs.FolderId;
            const key = folderId != null ? String(folderId) : '';
            if (key && byFolder.has(key)) {
                byFolder.get(key).push(rs);
            } else {
                unfiled.push(rs);
            }
        });
        return { byFolder, unfiled };
    }, [familyFolders, sortedRuleSets]);

    const visibleFolders = useMemo(() => {
        if (!filterActive) return familyFolders;
        return familyFolders.filter(folder => (groupedRuleSets.byFolder.get(String(folder.id)) || []).length > 0);
    }, [filterActive, familyFolders, groupedRuleSets]);

    useEffect(() => {
        const familyId = activeFamily?.id;
        if (!familyId) {
            setExpandedFolderIds(new Set());
            return;
        }
        try {
            const raw = sessionStorage.getItem(`ruleset-folders-expanded:${familyId}`);
            if (raw) {
                setExpandedFolderIds(new Set(JSON.parse(raw)));
                return;
            }
        } catch {
            // ignore invalid session state
        }
        setExpandedFolderIds(new Set(familyFolders.map(folder => String(folder.id))));
    }, [activeFamily?.id, familyFolders]);

    useEffect(() => {
        if (!filterActive) return;
        setExpandedFolderIds(prev => {
            const next = new Set(prev);
            visibleFolders.forEach(folder => next.add(String(folder.id)));
            return next;
        });
    }, [filterActive, visibleFolders]);

    const persistExpanded = useCallback((next) => {
        setExpandedFolderIds(next);
        const familyId = activeFamily?.id;
        if (!familyId) return;
        try {
            sessionStorage.setItem(`ruleset-folders-expanded:${familyId}`, JSON.stringify([...next]));
        } catch {
            // ignore quota / private mode
        }
    }, [activeFamily?.id]);

    const toggleFolderExpanded = useCallback((folderId) => {
        const key = String(folderId);
        persistExpanded(new Set(
            expandedFolderIds.has(key)
                ? [...expandedFolderIds].filter(id => id !== key)
                : [...expandedFolderIds, key]
        ));
    }, [expandedFolderIds, persistExpanded]);

    const clearDragState = useCallback(() => {
        setDropTargetKey(null);
        setDraggingFromFolderId(null);
    }, []);

    const isDragFromFolder = useCallback((folderId) => (
        String(draggingFromFolderId || '') === String(folderId)
    ), [draggingFromFolderId]);

    const handleFolderDragOver = useCallback((event, folderId) => {
        event.stopPropagation();
        if (isDragFromFolder(folderId)) {
            setDropTargetKey(null);
            return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDropTargetKey(folderId);
    }, [isDragFromFolder]);

    const handleFolderDragLeave = useCallback((event, folderId) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        setDropTargetKey(prev => (prev === folderId ? null : prev));
    }, []);

    const handleFolderDrop = useCallback((event, folderId) => {
        event.preventDefault();
        event.stopPropagation();
        const ruleSetId = event.dataTransfer.getData('ruleSetId');
        const sourceFolderId = event.dataTransfer.getData('sourceFolderId');
        clearDragState();
        if (!ruleSetId || String(sourceFolderId || '') === String(folderId)) return;
        handleMoveRuleSetToFolder(ruleSetId, folderId);
    }, [clearDragState, handleMoveRuleSetToFolder]);

    const handleUnfiledDragOver = useCallback((event) => {
        if (!draggingFromFolderId) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDropTargetKey(UNFILED_DROP);
    }, [draggingFromFolderId]);

    const handleUnfiledDrop = useCallback((event) => {
        event.preventDefault();
        const ruleSetId = event.dataTransfer.getData('ruleSetId');
        const sourceFolderId = event.dataTransfer.getData('sourceFolderId');
        clearDragState();
        if (!ruleSetId || !sourceFolderId) return;
        handleMoveRuleSetToFolder(ruleSetId, null);
    }, [clearDragState, handleMoveRuleSetToFolder]);

    const renderRuleSetRow = (rs) => {
        const isSelected = String(activeRuleSetId) === String(rs.id);
        const hasEmptyCells = isSelected && draftRuleSetData && String(draftRuleSetData.id) === String(rs.id)
            ? rulesetHasEmptyCells(draftRuleSetData)
            : !!(rs.hasEmptyCells || rs.HasEmptyCells);
        const matchHits = filterActive
            ? getRuleSetMatchHits(rs, {
                mode: rulesetSearchMode,
                query: rulesetSearchQuery,
                featureFilters: rulesetFeatureFilters,
                valueFilters: rulesetValueFilters
            })
            : [];
        const currentFolderId = rs.folderId ?? rs.FolderId;
        const moveItems = familyFolders
            .filter(folder => String(folder.id) !== String(currentFolderId || ''))
            .map(folder => ({
                label: `Move to ${folder.name}`,
                icon: 'fa-regular fa-folder',
                onSelect: () => handleMoveRuleSetToFolder(rs.id, folder.id)
            }));
        if (currentFolderId && familyFolders.some(folder => String(folder.id) === String(currentFolderId))) {
            moveItems.push({
                label: 'Remove from folder',
                icon: 'fa-solid fa-folder-minus',
                onSelect: () => handleMoveRuleSetToFolder(rs.id, null)
            });
        }

        const selectRuleSet = () => {
            if (String(activeRuleSetId) !== String(rs.id)) {
                setSearchHighlight?.(null);
            }
            setActiveRuleSetId(rs.id);
        };

        return (
            <div
                key={rs.id}
                draggable={true}
                onDragStart={(e) => {
                    e.dataTransfer.setData('sourceFamilyId', activeFamily.id);
                    e.dataTransfer.setData('ruleSetId', rs.id);
                    e.dataTransfer.setData('sourceFolderId', currentFolderId != null ? String(currentFolderId) : '');
                    e.dataTransfer.effectAllowed = 'move';
                    setDraggingFromFolderId(currentFolderId != null ? String(currentFolderId) : null);
                }}
                onDragEnd={clearDragState}
                onClick={selectRuleSet}
                onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    selectRuleSet();
                }}
                tabIndex={0}
                className={`rule-list-item-hover clickable-row${isSelected ? ' is-selected' : ''}`}
                style={{
                    padding: '0.25rem 0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    position: 'relative'
                }}
            >
                <i className="fa-regular fa-table" style={{ width: '1rem', textAlign: 'center', flexShrink: 0 }}></i>
                <span className="row-title" style={{ flex: 1, fontSize: '0.9rem' }}>
                    {rs.name}
                </span>
                {matchHits.length > 0 && (
                    <RuleSetSearchHitHint
                        hits={matchHits}
                        onReveal={(revealedHits) => {
                            setActiveRuleSetId(rs.id);
                            setSearchHighlight?.(buildSearchHighlight(rs.id, revealedHits));
                        }}
                    />
                )}
                {hasEmptyCells && (
                    <i
                        className="fa-solid fa-triangle-exclamation"
                        title="This rule table has empty cells."
                        style={{ color: '#D97706', fontSize: '0.85rem', flexShrink: 0 }}
                    ></i>
                )}
                <ActionsMenu
                    label={`${rs.name} actions`}
                    items={[
                        {
                            label: 'Copy to clipboard',
                            icon: 'fa-solid fa-copy',
                            onSelect: () => handleCopyRuleSetToClipboard(rs.id)
                        },
                        {
                            label: 'Import (overwrite)',
                            icon: 'fa-solid fa-file-import',
                            onSelect: () => setOverwriteTarget({ id: rs.id, name: rs.name })
                        },
                        {
                            label: 'Add comment',
                            icon: 'fa-solid fa-note-sticky',
                            onSelect: () => handleOpenAddComment(rs)
                        },
                        {
                            label: 'Rename',
                            icon: 'fa-solid fa-pen',
                            onSelect: () => handleOpenRenameRuleSet(rs)
                        },
                        ...moveItems,
                        {
                            label: 'Delete Table',
                            icon: 'fa-solid fa-trash-can',
                            danger: true,
                            onSelect: () => handleDeleteRuleSet(rs.id, rs.name)
                        }
                    ]}
                />
            </div>
        );
    };

    const sortedFamilies = useMemo(() => sortByName(families, 'name'), [families]);

    const toggleSortOrder = useCallback(() => {
        setRulesetSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    }, [setRulesetSortOrder]);

    const handleQuickSearchChange = useCallback((value) => {
        setRulesetSearchQuery(value);
        if (rulesetSearchMode === 'features' || rulesetSearchMode === 'values') {
            setRulesetSearchMode('all');
            setRulesetFeatureFilters([]);
            setRulesetValueFilters([]);
        } else if (rulesetSearchMode !== 'name' && rulesetSearchMode !== 'comments') {
            setRulesetSearchMode('all');
        }
    }, [rulesetSearchMode, setRulesetSearchQuery, setRulesetSearchMode, setRulesetFeatureFilters, setRulesetValueFilters]);

    const handleAdvancedApply = useCallback(({ mode, query, featureFilters, valueFilters }) => {
        setRulesetSearchMode(mode);
        setRulesetSearchQuery(query || '');
        setRulesetFeatureFilters(featureFilters || []);
        setRulesetValueFilters(valueFilters || []);
        setSearchHighlight?.(null);
    }, [setRulesetSearchMode, setRulesetSearchQuery, setRulesetFeatureFilters, setRulesetValueFilters, setSearchHighlight]);

    const handleClearFilter = useCallback(() => {
        setRulesetSearchMode('all');
        setRulesetSearchQuery('');
        setRulesetFeatureFilters([]);
        setRulesetValueFilters([]);
        setSearchHighlight?.(null);
    }, [setRulesetSearchMode, setRulesetSearchQuery, setRulesetFeatureFilters, setRulesetValueFilters, setSearchHighlight]);

    const handleToggleSearch = useCallback(() => {
        if (searchOpen) {
            if (rulesetSearchMode === 'all') setRulesetSearchQuery('');
            setSearchOpen(false);
            return;
        }
        setSearchOpen(true);
    }, [searchOpen, rulesetSearchMode, setRulesetSearchQuery]);

    const handleCloseFilter = useCallback(() => {
        setFilterOpen(false);
    }, []);

    const filterSummary = useMemo(() => {
        const parts = [];
        if (rulesetSearchMode === 'name' && rulesetSearchQuery.trim()) {
            parts.push(`Name: “${rulesetSearchQuery.trim()}”`);
        } else if (rulesetSearchMode === 'comments' && rulesetSearchQuery.trim()) {
            parts.push(`Comment: “${rulesetSearchQuery.trim()}”`);
        } else if (rulesetSearchMode === 'values' && rulesetSearchQuery.trim()) {
            parts.push(`Value: “${rulesetSearchQuery.trim()}”`);
        } else if (rulesetSearchQuery.trim()) {
            parts.push(`Search: “${rulesetSearchQuery.trim()}”`);
        }
        if (rulesetFeatureFilters.length > 0) {
            parts.push(`Features: ${rulesetFeatureFilters.join(' + ')}`);
        }
        if (rulesetValueFilters.length > 0) {
            parts.push(`Values: ${rulesetValueFilters.join(' + ')}`);
        }
        return parts.join(' · ');
    }, [rulesetSearchMode, rulesetSearchQuery, rulesetFeatureFilters, rulesetValueFilters]);

    return (
        <div style={{
            width: '320px',
            minWidth: '320px',
            borderRight: '1px solid var(--border)',
            background: 'var(--bg-card)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
        }}>

            {/* ============================================================ */}
            {/* HEADER */}
            {/* ============================================================ */}
            <div style={{
                padding: '1.5rem',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'var(--bg-card)'
            }}>
                <div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Rules</h2>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>Decision Matrices</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <button
                        type="button"
                        className="actions-menu-trigger"
                        title="Run Simulator"
                        aria-label="Run Simulator"
                        onClick={() => {
                            if (activeFamily) navigate(`/simulator?familyId=${activeFamily.id}`);
                            else navigate('/simulator');
                        }}
                    >
                        <i className="fa-solid fa-flask-gear"></i>
                    </button>
                    <RuleImporter
                        activeFamilyId={currentContext?.id}
                        families={families}
                        featuresMetadata={featuresMetadata}
                        onImportComplete={() => { loadMenu(); onDataChanged(); }}
                    />
                    <button
                        onClick={(e) => {
                            if (activeFamily) handleOpenModal(e, 'createRuleset', activeFamily);
                        }}
                        disabled={!activeFamily || !canEditCurrent}
                        className="submit-btn"
                        style={{
                            margin: 0,
                            padding: '0.5rem 0.75rem',
                            fontSize: '0.85rem',
                            ...(activeFamily ? {} : {
                                background: 'var(--bg-secondary)',
                                color: 'var(--text-secondary)',
                                boxShadow: 'none'
                            }),
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                        }}
                    >
                        <i className="fa-solid fa-plus"></i> New
                    </button>
                </div>
            </div>

            {/* ============================================================ */}
            {/* FAMILY SELECTOR & ACTION BUTTONS */}
            {/* ============================================================ */}
            <div style={{
                padding: '1rem 1.5rem 1.5rem',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                background: 'var(--bg-card)',
                position: 'relative',
                zIndex: 2
            }}>
                <div>
                    <label htmlFor="rules-family-select" style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        Family
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%' }}>
                    {/* Family Selector */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <select
                            id="rules-family-select"
                            className="input-field"
                            value={currentContext?.id || ''}
                            onChange={(e) => {
                                const selectedFam = sortedFamilies.find(f => f.id.toString() === e.target.value);
                                if (selectedFam) {
                                    setCurrentContext({ id: selectedFam.id, name: selectedFam.name });
                                }
                            }}
                            style={{
                                width: '100%',
                                padding: '0.4rem 0.5rem',
                                borderRadius: 'var(--radius)',
                                border: '1px solid var(--border)',
                                background: 'var(--bg-card)',
                                color: 'var(--text-primary)',
                                fontSize: '0.85rem',
                                outline: 'none',
                                margin: 0,
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {sortedFamilies.length === 0 ? (
                                <option value="">No Families Linked</option>
                            ) : (
                                sortedFamilies.map(f => (
                                    <option key={f.id} value={f.id}>{f.name}</option>
                                ))
                            )}
                        </select>
                    </div>

                    {/* Active Family Actions */}
                    {currentContext?.id && (
                        <div ref={productActionsRef} style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                            <ActionsMenu
                                label="Active family actions"
                                onOpenChange={(open) => {
                                    if (open) setFilterOpen(false);
                                }}
                                items={[
                                    {
                                        label: 'Search',
                                        icon: 'fa-solid fa-magnifying-glass',
                                        onSelect: handleToggleSearch
                                    },
                                    {
                                        label: 'Filter',
                                        icon: 'fa-solid fa-filter',
                                        onSelect: () => setFilterOpen(true)
                                    },
                                    {
                                        label: 'Sort',
                                        icon: 'fa-solid fa-arrow-down-a-z',
                                        onSelect: () => setSortOpen(open => !open)
                                    },
                                    {
                                        label: 'New folder',
                                        icon: 'fa-regular fa-folder',
                                        onSelect: () => {
                                            if (activeFamily) handleOpenCreateFolder(activeFamily);
                                        }
                                    },
                                    {
                                        label: 'Check Consistency',
                                        icon: 'fa-solid fa-list-check',
                                        onSelect: (event) => {
                                            const activeFamily = families.find(f => f.id.toString() === currentContext.id.toString());
                                            if (activeFamily) handleCheckConsistency(event, activeFamily);
                                        }
                                    },
                                    {
                                        label: 'Delete Family',
                                        icon: 'fa-solid fa-trash-can',
                                        danger: true,
                                        onSelect: (event) => {
                                            const activeFamily = families.find(f => f.id.toString() === currentContext.id.toString());
                                            if (activeFamily) handleOpenModal(event, 'deleteFamily', activeFamily);
                                        }
                                    }
                                ]}
                            />
                            {filterOpen && (
                                <RulesetAdvancedSearch
                                    searchMode={rulesetSearchMode}
                                    searchQuery={rulesetSearchQuery}
                                    featureFilters={rulesetFeatureFilters}
                                    valueFilters={rulesetValueFilters}
                                    features={featureOptions}
                                    onApply={handleAdvancedApply}
                                    onClear={handleClearFilter}
                                    anchorRef={productActionsRef}
                                    onClose={handleCloseFilter}
                                />
                            )}
                        </div>
                    )}
                    </div>
                </div>

                {currentContext?.id && (searchOpen || sortOpen) && (
                    <SelectorSearchSortBar
                        searchQuery={rulesetSearchQuery}
                        onSearchChange={handleQuickSearchChange}
                        searchPlaceholder="Search tables..."
                        sortBy={rulesetSortBy}
                        onSortByChange={setRulesetSortBy}
                        sortOrder={rulesetSortOrder}
                        onSortOrderToggle={toggleSortOrder}
                        showSearch={searchOpen}
                        showSortButton={false}
                        sortOpen={sortOpen}
                        autoFocus={searchOpen}
                        sortOptions={[
                            { value: 'name', label: 'Name' },
                            { value: 'updatedAt', label: 'Date Modified' },
                            { value: 'createdAt', label: 'Date Created' },
                            { value: 'updatedBy', label: 'Created By' }
                        ]}
                    />
                )}
            </div>

            <div
                onDragOver={handleUnfiledDragOver}
                onDrop={handleUnfiledDrop}
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.25rem'
                }}
            >
                {filterActive && (
                    <div style={{
                        padding: '0.55rem 0.7rem',
                        background: 'var(--accent-glow)',
                        border: '1px solid var(--accent)',
                        borderRadius: 'var(--radius)',
                        fontSize: '0.75rem',
                        color: 'var(--text-primary)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.45rem'
                    }}>
                        <i className="fa-solid fa-filter" style={{ color: 'var(--accent)', marginTop: '0.15rem', flexShrink: 0 }}></i>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>
                                Showing {sortedRuleSets.length} of {allRuleSets.length} tables
                            </div>
                            {filterSummary && (
                                <div style={{ color: 'var(--text-secondary)', marginTop: '0.15rem', wordBreak: 'break-word' }}>
                                    {filterSummary}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={handleClearFilter}
                            title="Clear filter"
                            aria-label="Clear filter"
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--text-secondary)',
                                cursor: 'pointer',
                                padding: '0.1rem',
                                flexShrink: 0
                            }}
                        >
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>
                )}
                {sortedRuleSets.length === 0 && visibleFolders.length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                        {filterActive ? 'No rule tables match this filter.' : 'No rule tables found.'}
                    </div>
                ) : (
                    <>
                        {visibleFolders.map(folder => {
                            const folderKey = String(folder.id);
                            const isExpanded = expandedFolderIds.has(folderKey);
                            const folderTables = groupedRuleSets.byFolder.get(folderKey) || [];
                            const isDropTarget = dropTargetKey === folder.id;
                            return (
                                <div
                                    key={folder.id}
                                    onDragOver={(event) => handleFolderDragOver(event, folder.id)}
                                    onDragLeave={(event) => handleFolderDragLeave(event, folder.id)}
                                    onDrop={(event) => handleFolderDrop(event, folder.id)}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.35rem',
                                        borderRadius: 'var(--radius)',
                                        outline: isDropTarget ? '1px dashed var(--accent)' : 'none',
                                        background: isDropTarget ? 'var(--accent-glow)' : 'transparent'
                                    }}
                                >
                                    <div
                                        className="rule-list-item-hover clickable-row"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            padding: '0.25rem'
                                        }}
                                        onClick={() => toggleFolderExpanded(folder.id)}
                                        onKeyDown={(event) => {
                                            if (event.target !== event.currentTarget) return;
                                            if (event.key !== 'Enter' && event.key !== ' ') return;
                                            event.preventDefault();
                                            toggleFolderExpanded(folder.id);
                                        }}
                                        tabIndex={0}
                                        aria-expanded={isExpanded}
                                    >
                                        <i
                                            className={isExpanded ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'}
                                            style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', width: '0.7rem', flexShrink: 0 }}
                                        ></i>
                                        <i
                                            className={isExpanded ? 'fa-regular fa-folder-open' : 'fa-regular fa-folder'}
                                            style={{ color: 'var(--accent)', width: '1rem', textAlign: 'center', flexShrink: 0 }}
                                        ></i>
                                        <span className="row-title" style={{ flex: 1, fontSize: '0.85rem', fontWeight: 700 }}>
                                            {folder.name}
                                        </span>
                                        <span className="row-muted" style={{ fontSize: '0.7rem', flexShrink: 0 }}>
                                            {folderTables.length}
                                        </span>
                                        <ActionsMenu
                                            label={`${folder.name} folder actions`}
                                            items={[
                                                {
                                                    label: 'New table',
                                                    icon: 'fa-solid fa-plus',
                                                    onSelect: () => handleOpenCreateRulesetInFolder(activeFamily, folder)
                                                },
                                                {
                                                    label: 'Rename',
                                                    icon: 'fa-solid fa-pen',
                                                    onSelect: () => handleOpenRenameFolder(folder, activeFamily)
                                                },
                                                {
                                                    label: 'Delete folder',
                                                    icon: 'fa-solid fa-trash-can',
                                                    danger: true,
                                                    onSelect: () => handleDeleteFolder(folder)
                                                }
                                            ]}
                                        />
                                    </div>
                                    {isExpanded && (
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.35rem',
                                            marginLeft: '1.15rem',
                                            paddingLeft: '0.65rem',
                                            borderLeft: '1px solid var(--border)',
                                            minHeight: folderTables.length === 0 ? '2.25rem' : undefined
                                        }}>
                                            {folderTables.map(rs => renderRuleSetRow(rs))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {draggingFromFolderId && (
                            <div
                                onDragOver={handleUnfiledDragOver}
                                onDrop={handleUnfiledDrop}
                                style={{
                                    padding: '0.7rem 0.85rem',
                                    borderRadius: 'var(--radius)',
                                    border: '1px dashed var(--accent)',
                                    background: dropTargetKey === UNFILED_DROP ? 'var(--accent-glow)' : 'transparent',
                                    color: 'var(--text-secondary)',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    textAlign: 'center'
                                }}
                            >
                                Drop to remove from folder
                            </div>
                        )}
                        {groupedRuleSets.unfiled.map(rs => renderRuleSetRow(rs))}
                    </>
                )}
            </div>
            {overwriteTarget && (
                <ImportWizardModal
                    show={Boolean(overwriteTarget)}
                    onClose={() => setOverwriteTarget(null)}
                    onImportComplete={() => {
                        loadMenu();
                        onDataChanged();
                        if (String(overwriteTarget.id) === String(activeRuleSetId)) {
                            reloadActiveRuleSet();
                        }
                    }}
                    families={families}
                    featuresMetadata={featuresMetadata}
                    activeFamilyId={currentContext?.id}
                    targetRuleSet={overwriteTarget}
                />
            )}
        </div>
    );
}
